import type { InventoryUnit } from "../types";

type ToteShape = { width: number; height: number; depth: number };

const DEFAULT_SHAPE: ToteShape = { width: 34, height: 22, depth: 9 };

const TOTE_SHAPES: Record<string, ToteShape> = {
  "6.5 qt": { width: 18, height: 9, depth: 5 },
  "7 gal": { width: 23, height: 15, depth: 7 },
  "9 gal": { width: 25, height: 17, depth: 7 },
  "12 gal": { width: 28, height: 18, depth: 8 },
  "14 gal": { width: 30, height: 20, depth: 8 },
  "17 gal": { width: 32, height: 22, depth: 9 },
  "27 gal": { width: 28, height: 26, depth: 9 },
  "38 gal": { width: 40, height: 28, depth: 10 },
  "40 gal": { width: 50, height: 22, depth: 9 },
  "55 gal": { width: 46, height: 31, depth: 11 },
  "57 gal": { width: 48, height: 32, depth: 11 }
};

const LID_COLORS: Record<string, { top: string; front: string; side: string }> = {
  Yellow: { top: "#ffd338", front: "#e9a900", side: "#be7d00" },
  Olive: { top: "#98a35a", front: "#6f7b37", side: "#4e5824" },
  Red: { top: "#ef6a61", front: "#cb3f39", side: "#922b29" }
};
const UNKNOWN_LID = { top: "#aeb8c4", front: "#7d8998", side: "#586270" };

export function TotePixelIcon({
  size,
  binColor,
  lidColor,
  className = ""
}: {
  size: string | null;
  binColor: string | null;
  lidColor: string | null;
  className?: string;
}) {
  const shape = TOTE_SHAPES[size || ""] || DEFAULT_SHAPE;
  const lid = LID_COLORS[lidColor || ""] || UNKNOWN_LID;
  const clear = binColor === "Transparent";
  const body = clear
    ? { front: "#c9e3e8", side: "#91b9c3", edge: "#527d88", highlight: "#effcfe" }
    : { front: "#292e33", side: "#171b1f", edge: "#080a0c", highlight: "#4c555c" };
  const x = (64 - shape.width - shape.depth) / 2;
  const baseY = 44;
  const topY = baseY - shape.height;
  const rightX = x + shape.width;
  const backY = topY - shape.depth / 2;
  const label = `${size || "Custom size"} ${clear ? "clear" : "black"} bin with ${(lidColor || "unspecified").toLowerCase()} lid`;

  return (
    <svg
      className={`tote-pixel-icon ${clear ? "is-clear" : "is-black"} ${className}`.trim()}
      viewBox="0 0 64 48"
      role="img"
      aria-label={label}
      data-tote-size={size || "custom"}
      data-proportional-width={shape.width}
      data-proportional-height={shape.height}
      shapeRendering="crispEdges"
    >
      <polygon
        points={`${x + 2},${topY + 2} ${rightX - 2},${topY + 2} ${rightX - 4},${baseY} ${x + 4},${baseY}`}
        fill={body.front}
        stroke={body.edge}
        strokeWidth="1.5"
      />
      <polygon
        points={`${rightX - 2},${topY + 2} ${rightX + shape.depth - 2},${backY + 2} ${rightX + shape.depth - 4},${baseY - shape.depth / 2} ${rightX - 4},${baseY}`}
        fill={body.side}
        stroke={body.edge}
        strokeWidth="1.5"
      />
      {clear ? (
        <>
          <polygon points={`${x + 7},${topY + 7} ${rightX - 8},${topY + 7} ${rightX - 9},${baseY - 5} ${x + 8},${baseY - 5}`} fill="#ffffff" opacity=".28" />
          <rect x={x + 7} y={topY + 8} width="3" height={Math.max(shape.height - 15, 3)} fill={body.highlight} opacity=".8" />
          <rect x={rightX - 7} y={topY + 7} width="2" height={Math.max(shape.height - 14, 3)} fill="#739aa4" opacity=".55" />
        </>
      ) : (
        <>
          <rect x={x + 6} y={topY + 7} width="3" height={Math.max(shape.height - 14, 3)} fill={body.highlight} />
          <rect x={rightX - 7} y={topY + 7} width="2" height={Math.max(shape.height - 14, 3)} fill="#111519" />
        </>
      )}
      <rect x={x + 3} y={topY + 3} width={shape.width - 6} height="3" fill={clear ? "#789ca4" : "#111519"} opacity={clear ? ".7" : "1"} />
      <polygon
        points={`${x - 1},${topY} ${x + shape.depth - 2},${backY} ${rightX + shape.depth + 1},${backY} ${rightX + 2},${topY}`}
        fill={lid.top}
        stroke={lid.side}
        strokeWidth="1.5"
      />
      <rect x={x - 2} y={topY} width={shape.width + 5} height="4" fill={lid.front} stroke={lid.side} strokeWidth="1" />
      <polygon
        points={`${rightX + 2},${topY} ${rightX + shape.depth + 1},${backY} ${rightX + shape.depth + 1},${backY + 4} ${rightX + 2},${topY + 4}`}
        fill={lid.side}
      />
      <rect x={x + 5} y={topY + 1} width={Math.max(shape.width - 12, 8)} height="1" fill="#fff" opacity=".32" />
      <rect x={x + 4} y={baseY - 2} width="5" height="2" fill={body.edge} />
      <rect x={rightX - 10} y={baseY - 2} width="5" height="2" fill={body.edge} />
    </svg>
  );
}

export function InventoryToteIcon({ unit, className }: { unit: InventoryUnit; className?: string }) {
  if (unit.inventory_type !== "TOTE") return null;
  return (
    <TotePixelIcon
      size={unit.container_size}
      binColor={unit.container_color}
      lidColor={unit.lid_color}
      className={className}
    />
  );
}
